import { ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, Input, KeyValueDiffers, OnChanges, OnInit, Output, ViewChild } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';

import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { ChartExportEventModel } from '@Common/Charts/models/chart-export-event.model';
import { ReportBlockTypeEnum } from '@Common/Reports/enums/report-block-type.enum';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { SocialChartExportModel, SocialChartExportService } from '@Social/Intelligence/services/social-chart-export.service';
import { BlockComponent } from '@Common/PageBuilder/components/Block/block.component';
import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';
import { ClickOutsideService } from '@Common/PageBuilder/services/click-outside.service';
import { ChartDisplayElementModel, ChartValidSourceStateEnum } from '@Common/Reports/models/chart-display-element.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { ReportsSeriesPreviewNewService } from '@Common/Reports/services/reports-series-preview-new.service';
import { TopEntitiesChartComponent } from '@Common/Charts/components/TopEntitiesChart/top-entities-chart.component';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';


@Component({
    selector: 'agpr-top-entities-chart-block',
    templateUrl: './top-entities-chart-block.component.html',
    styleUrls: ['./top-entities-chart-block.component.scss'],
    standalone: false
})
export class TopEntitiesChartBlockComponent extends BlockComponent implements OnInit, OnChanges {

    @Input() series: ReportSeriesModel;
    @Input() entityType: EntityType;
    @Input() metricSelectable: boolean;
    @Input() bigProfileImage: boolean;
    @Input() height: number = 495;
    @Input() canViewProfileDetail: boolean;
    @Input() shouldValidateSeries: boolean = true;

    @Output() seriesChange: EventEmitter<{
        newSeries: ReportSeriesModel;
        entityType: EntityType;
        sentimentType?: SearchCriteriaQuerySentimentsEnum;
    }> = new EventEmitter();

    displayElement: ChartDisplayElementModel;

    readonly reportBlockTypes = ReportBlockTypeEnum;
    readonly entityTypes = EntityType;
    readonly messageSeverities = messageSeverityEnum;

    @ViewChild('topEntitiesChart') topEntitiesChart: TopEntitiesChartComponent;

    constructor(
        public localizationService: LocalizationService,
        public chartExportService: SocialChartExportService,
        public reporstSeriesPreviewService: ReportsSeriesPreviewNewService,
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        protected changeDetectorRef: ChangeDetectorRef,
        public clickOutsideService: ClickOutsideService,
        public el: ElementRef
    ) {
        super(
            differs,
            pageBuilderService,
            changeDetectorRef,
            clickOutsideService,
            el
        );
    }

    ngOnInit(): void {
        this.displayElement = this.block?.displayElement as ChartDisplayElementModel;
        super.ngOnInit();
    }

    ngOnChanges(): void {
        const validSourceState = (<ChartDisplayElementModel>this.block.displayElement).checkValidSourcesForChartType(<ReportBlockTypeEnum>this.block.type.key);
        this.renderable = validSourceState !== ChartValidSourceStateEnum.NONE;
        if (this.preview && this.displayElement?.series) {
            this.series = this.reporstSeriesPreviewService.getPreviewSeries(this.displayElement.series, this.block)[0];
        }
    }

    public async exportChart(event: ChartExportEventModel, chartElementRef: HTMLElement) {
        let exportTitle;
        if (this.metricSelectable) {
            const baseTitle = this.localizationService.transform('Monitor.MAD.topEntities.title.' + this.entityType);
            const metricLabel = this.localizationService.transform(this.topEntitiesChart.selectedMetric?.label);
            exportTitle = `${baseTitle} ${metricLabel}`;
        } else {
            exportTitle = (this.block.displayElement as ChartDisplayElementModel).title;
        }
        const exportDataArray = [];
        this.topEntitiesChart.dataPoints.forEach(data => {
            const obj = {};
            obj['termName'] = data.label;
            obj['termValue'] = data.value;
            exportDataArray.push(obj);
        });
        const exportModel: SocialChartExportModel = {
            title: exportTitle,
            exportData: exportDataArray,
            exportLabelDefinitions: new Map<string, string>([
                ['termName', this.localizationService.transform('Social.export.label.termName')],
                ['termValue', this.localizationService.transform('Social.export.label.termValue')]
            ])
        };

        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        if (exportType === 'csv' || exportType === 'json') {
            this.chartExportService.exportData(exportType, exportModel);
        } else {
            await this.chartExportService.exportImageSnapDomWithOverrides(
                exportType,
                chartElementRef,
                exportModel.title,
                [
                    { selector: '.agpr-topEntitiesChartBlock-exportMenu', style: { display: 'none' } },
                    { selector: '.agpr-dropdown-triggerIcon', style: { display: 'none' } }
                ]
            );
        }
    }
}
